import { useState } from "react";
import { CheckCircle2, Download, Eye, FileText, PenLine } from "lucide-react";

import type { PolicyDoc, PolicyKind, SignedPolicy } from "../lib/policySigning.js";
import { PolicyContentView } from "./PolicyContentView.js";
import { PolicyDocumentViewer } from "./PolicyDocumentViewer.js";
import { AsyncButton } from "./ui/async-button.js";
import { Badge } from "./ui/badge.js";
import { Button } from "./ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card.js";
import { Checkbox } from "./ui/checkbox.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog.js";

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

// The documents an employee still has to sign, one card each, with a button
// that opens the document in a dialog. Under the document in the dialog: an
// "I have read and agree" box (enabled once they have scrolled to its end),
// then their signature (their name on record) and a Sign button. Used by the
// onboarding wizard, the emailed signing page and the in-app banner, so every
// signing path asks for the same thing.
export function PolicySignPanel({ outstanding, signerName, onOpened, onSign }: {
  outstanding: PolicyDoc[];
  signerName: string;
  /** Called when a document is first opened. */
  onOpened?: (kind: PolicyKind) => void;
  /** Must return the promise and reject on failure (AsyncButton contract). */
  onSign: (kind: PolicyKind) => Promise<unknown>;
}) {
  return (
    <div className="space-y-5">
      {outstanding.map(doc => (
        <PolicySignCard key={doc.kind} doc={doc} signerName={signerName} onOpened={onOpened} onSign={onSign} />
      ))}
    </div>
  );
}

function PolicySignCard({ doc, signerName, onOpened, onSign }: {
  doc: PolicyDoc;
  signerName: string;
  onOpened: ((kind: PolicyKind) => void) | undefined;
  onSign: (kind: PolicyKind) => Promise<unknown>;
}) {
  const [viewing, setViewing] = useState(false);
  const [opened, setOpened] = useState(false);
  const [read, setRead] = useState(false);
  const [agreed, setAgreed] = useState(false);

  function view() {
    setViewing(true);
    if (!opened) { setOpened(true); onOpened?.(doc.kind); }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-[15px]">{doc.title} <span className="font-normal text-surface-500">v{doc.version}</span></CardTitle>
          <Badge tone={doc.yearly ? "accent" : doc.allEmployees ? "info" : "neutral"}>
            {doc.yearly ? "Yearly re-sign" : doc.allEmployees ? "Required for all employees" : "Requested by HR"}
          </Badge>
        </div>
        {doc.typeName !== doc.title && <CardDescription>{doc.typeName}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-surface-200 bg-surface-50 px-3 py-2.5">
          <FileText size={18} className="shrink-0 text-surface-500" />
          <span className="min-w-0 flex-1 text-[13px] text-surface-700">
            {read
              ? <span className="inline-flex items-center gap-1 text-[var(--color-success-fg)]"><CheckCircle2 size={14} /> Read to the end</span>
              : "Open the document, read it to the end, then agree and sign."}
          </span>
          <Button size="sm" variant="outline" onClick={view}>
            <Eye size={14} /> View &amp; sign
          </Button>
        </div>
        <PolicyViewerDialog
          doc={doc} open={viewing} onOpenChange={setViewing}
          read={read} onReachedEnd={() => setRead(true)}
          agreed={agreed} onAgreedChange={setAgreed}
          signerName={signerName} onSign={() => onSign(doc.kind)}
        />
      </CardContent>
    </Card>
  );
}

// The document, loaded only when opened: its extracted text with headings and
// numbered clauses, or the PDF itself when the text wasn't read. Agreeing and
// signing happen under it; a signed document leaves the list, closing this.
function PolicyViewerDialog({ doc, open, onOpenChange, read, onReachedEnd, agreed, onAgreedChange, signerName, onSign }: {
  doc: PolicyDoc;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  read: boolean;
  onReachedEnd: () => void;
  agreed: boolean;
  onAgreedChange: (agreed: boolean) => void;
  signerName: string;
  /** Must return the promise and reject on failure (AsyncButton contract). */
  onSign: () => Promise<unknown>;
}) {
  const checkboxId = `policy-agree-${doc.kind}`;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[88vh] max-h-[88vh] w-[95vw] max-w-4xl flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{doc.title} <span className="font-normal text-surface-500">v{doc.version}</span></DialogTitle>
          <DialogDescription>{read ? "You have reached the end. Agree below, then sign." : "Scroll to the end of the document to agree and sign."}</DialogDescription>
        </DialogHeader>
        {open && (doc.content?.length ? (
          <PolicyContentView title={doc.title} blocks={doc.content} onReachedEnd={onReachedEnd} className="min-h-0 flex-1 rounded-md border border-surface-200" />
        ) : (
          <PolicyDocumentViewer url={doc.readUrl} onReachedEnd={onReachedEnd} className="min-h-0 flex-1 overflow-hidden rounded-md border border-surface-200" />
        ))}
        <div className="space-y-3 border-t border-surface-200 pt-3">
          <div className="flex items-start gap-2">
            <Checkbox id={checkboxId} checked={agreed} disabled={!read} onCheckedChange={(v) => onAgreedChange(v === true)} />
            <label htmlFor={checkboxId} className={read ? "text-[13px] text-surface-800" : "text-[13px] text-surface-400"}>
              I have read and agree to the {doc.title}.
            </label>
          </div>
          {agreed && !signerName && (
            <p className="text-sm text-[var(--color-error-fg)]">Your name is missing from your employee record. Ask HR to add it, then sign.</p>
          )}
          <div className="flex flex-wrap items-end justify-between gap-3">
            {agreed && signerName ? <SignaturePreview name={signerName} /> : <span />}
            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <AsyncButton onClick={() => onSign()} disabled={!agreed || !signerName}>
                <PenLine size={14} /> Sign
              </AsyncButton>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// The signature as it will be stamped on the signed PDF (lib/handbookSigning.ts
// on the API): the name in the signature font over a line, with the
// "Digitally signed by … on …" caption.
function SignaturePreview({ name }: { name: string }) {
  return (
    <div className="max-w-sm rounded-md border border-dashed border-surface-300 bg-surface-0 px-4 pt-3 pb-2" aria-label={`Signature preview: ${name}`}>
      <p className="truncate font-signature text-3xl leading-tight text-brand-900">{name}</p>
      <div className="mt-1 border-t border-surface-300" />
      <p className="mt-1 text-[11px] text-surface-500">Digitally signed by {name} on {fmtDate(new Date().toISOString())}</p>
    </div>
  );
}

// The signed copies, each with a download icon. Shown wherever signing
// finishes and on the HR profile.
export function SignedPolicyDownloads({ signed }: { signed: SignedPolicy[] }) {
  if (signed.length === 0) return null;
  return (
    <ul className="space-y-2">
      {signed.map(s => (
        <li key={s.kind} className="flex flex-wrap items-center gap-2">
          <CheckCircle2 size={14} className="shrink-0 text-[var(--color-success-fg)]" />
          <span className="min-w-0 flex-1 text-[13px]">
            <span className="font-medium">{s.title}{s.version && ` v${s.version}`}</span>
            <span className="text-surface-500"> · signed{s.signedName && ` by ${s.signedName}`} on {fmtDate(s.signedAt)}</span>
          </span>
          {s.downloadUrl && <SignedCopyDownload url={s.downloadUrl} title={s.title} />}
        </li>
      ))}
    </ul>
  );
}

export function SignedCopyDownload({ url, title }: { url: string; title: string }) {
  return (
    <Button size="sm" variant="secondary" className="w-8 px-0" asChild>
      <a href={url} download aria-label={`Download signed ${title}`} title={`Download signed ${title}`}>
        <Download size={14} />
      </a>
    </Button>
  );
}
